/**
 * 딥링크 관련 유틸리티 함수들
 */

// 기본 설정
const APP_CONFIG = {
  scheme: 'lengsup',
  packageName: 'com.lengsup.app',
  bundleId: 'com.lengsup.app',
  webDomain: process.env.REACT_APP_WEB_DOMAIN || 'lengsup-admin.web.app',
  appStoreId: process.env.REACT_APP_APP_STORE_ID || '123456789', // 실제 App Store ID로 변경 필요
  playStoreUrl: `https://play.google.com/store/apps/details?id=com.lengsup.app`
};

/**
 * 친구 초대 링크 생성 (앱에서 사용)
 * @param {Object} params - 초대 링크 파라미터
 * @param {string} params.userId - 사용자 ID
 * @param {string} params.userName - 사용자 이름
 * @returns {string} 생성된 초대 링크
 */
export const generateInviteLink = ({ userId, userName }) => {
  const baseUrl = `https://${APP_CONFIG.webDomain}/invite`;
  const params = new URLSearchParams();
  
  if (userId) params.append('userId', userId);
  if (userName) params.append('userName', userName);
  
  return `${baseUrl}?${params.toString()}`;
};

/**
 * 딥링크 URL 생성 (앱 스킴용)
 * @param {Object} params - 딥링크 파라미터
 * @param {string} params.userId - 사용자 ID
 * @param {string} params.userName - 사용자 이름
 * @returns {string} 딥링크 URL
 */
export const generateDeepLink = ({ userId, userName }) => {
  const params = new URLSearchParams();
  if (userId) params.append('userId', userId);
  if (userName) params.append('userName', userName);
  
  return `${APP_CONFIG.scheme}://invite?${params.toString()}`;
};

/**
 * Android Intent URL 생성
 * @param {Object} params - Intent 파라미터
 * @param {string} params.userId - 사용자 ID
 * @param {string} params.userName - 사용자 이름
 * @returns {string} Android Intent URL
 */
export const generateAndroidIntent = ({ userId, userName }) => {
  const params = new URLSearchParams();
  if (userId) params.append('userId', userId);
  if (userName) params.append('userName', userName);
  
  return `intent://invite?${params.toString()}#Intent;scheme=${APP_CONFIG.scheme};package=${APP_CONFIG.packageName};end`;
};

/**
 * 스토어 URL 가져오기
 * @returns {Object} iOS/Android 스토어 URL
 */
export const getStoreUrls = () => ({
  ios: `https://apps.apple.com/app/lengsup/id${APP_CONFIG.appStoreId}`,
  android: APP_CONFIG.playStoreUrl
});

/**
 * 사용자 기기 감지
 * @returns {Object} 기기 정보
 */
export const detectDevice = () => {
  const userAgent = navigator.userAgent;
  
  return {
    isIOS: /iPad|iPhone|iPod/.test(userAgent),
    isAndroid: /Android/.test(userAgent),
    isMobile: /iPad|iPhone|iPod|Android/.test(userAgent),
    isDesktop: !/iPad|iPhone|iPod|Android/.test(userAgent)
  };
};

/**
 * 앱 설치 여부 확인 (실험적)
 * @param {string} deepLinkUrl - 딥링크 URL
 * @returns {Promise<boolean>} 앱 설치 여부
 */
export const checkAppInstalled = (deepLinkUrl) => {
  return new Promise((resolve) => {
    const startTime = Date.now();
    const timeout = 2000; // 2초 타임아웃
    
    // 페이지가 숨겨지면 앱이 열린 것으로 간주
    const handleVisibilityChange = () => {
      if (document.hidden) {
        resolve(true);
        cleanup();
      }
    };
    
    // 타임아웃 처리
    const timeoutId = setTimeout(() => {
      const elapsed = Date.now() - startTime;
      // 2초 이상 걸렸으면 앱이 없는 것으로 간주
      resolve(elapsed < timeout);
      cleanup();
    }, timeout);
    
    const cleanup = () => {
      clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    
    // 이벤트 리스너 등록
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // 딥링크 실행
    try {
      window.location.href = deepLinkUrl;
    } catch (error) {
      resolve(false);
      cleanup();
    }
  });
};

export default {
  generateInviteLink,
  generateDeepLink,
  generateAndroidIntent,
  getStoreUrls,
  detectDevice,
  checkAppInstalled
};